import React from 'react';
import { Document } from 'react-pdf';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import PdfPage from './PdfPage';
import { IDocument, IBlock, ISignatureSettings } from '../store/types';
import { makeStyles } from '@material-ui/styles';
import pdfCache from '../lib/pdfCache';
import { IBlockState } from '../store/reducers/blocks';
import { IGroupState } from '../store/reducers/groups';
import { IEnvelopeState } from '../store/reducers/envelope';
import { MD_MEDIA_QUERY, SKELETON_HEIGHT, SKELETON_WIDTH, IS_IE } from '../common/constants';
import Skeleton from '@material-ui/lab/Skeleton';
import { boxShadows } from '../theme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import scrollIntoView from 'scroll-into-view';
import { AdPlacementIds, useSessionStorageAdContext } from './DigiSignAdsProvider';

interface StyleProps {
  isTopAdRevealed: boolean;
  isMobile: boolean;
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  container: {
    position: 'fixed',
    top: (props: StyleProps) => {
      const { isTopAdRevealed, isMobile } = props;
      if (isMobile) {
        return isTopAdRevealed ? 155 : 50;
      } else {
        return isTopAdRevealed ? 195 : 115;
      }
    },
    bottom: 80,
    left: 0,
    right: 0,
    overflow: 'auto',
  },
  document: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  documentPadding: {
    padding: '0 8px',
  },
  skeleton: {
    boxShadow: boxShadows.main,
    backgroundColor: 'rgba(255,255,255,0.25)',
    margin: '48px auto 0',
  },
  hidden: {
    display: 'none',
  },
}));

interface IProps {
  documents: IDocument[];
  zoom: number;
  clickSignature: Function;
  updateBlock: Function;
  blocks: IBlockState;
  groups?: IGroupState;
  envelope: IEnvelopeState;
  signingExperienceBlocks: IBlock[];
  allBlocksComplete: boolean;
  currentSelectedBlockIndex: number | null;
  signingExperienceComplete: boolean;
  scrollToNextBlock: () => void;
  tabToBlock: (nextIndex: number) => void;
  handlePageLoad: (page: any, fullPage: number) => void;
  pageDimensions: { width?: number; height?: number };
  pageScale: { [pageNumber: number]: number };
  setPageScale: (fullPage: number, pageScale: number) => void;
  signatureSettings?: ISignatureSettings;
  setBlocksInGroupToComplete: (blocks: IBlock[]) => void;
  setCurrentDocument: (documentId: string, documentName: string) => void;
}

const PdfViewer = (props: IProps) => {
  const { adVisibleCollection } = useSessionStorageAdContext();
  const isMobile = useMediaQuery(MD_MEDIA_QUERY);
  const classes = useStyles({
    isTopAdRevealed: adVisibleCollection[AdPlacementIds.SIGNING],
    isMobile: isMobile,
  });
  const [firstPageLoaded, setFirstPageLoaded] = React.useState(false);
  const [visiblePage, setVisiblePage] = React.useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = React.useState(false);
  const {
    envelope,
    blocks,
    documents,
    signingExperienceBlocks,
    currentSelectedBlockIndex,
    signingExperienceComplete,
    groups,
    setCurrentDocument,
  } = props;

  const zoom = props.zoom;

  const scrollToBlock = (blockElement: HTMLDivElement, fullPage: number) => {
    setIsAutoScrolling(true);
    scrollIntoView(blockElement, () => {
      setIsAutoScrolling(false);
      setVisiblePage(fullPage);
    });
    if (!IS_IE) {
      blockElement.focus({ preventScroll: true });
    }
  };

  const handleSetVisiblePage = (fullPage: number, documentId: string, documentName: string) => {
    setCurrentDocument(documentId, documentName);
    if (!isAutoScrolling) {
      setVisiblePage(fullPage);
    }
  };

  const getGroups = (documentId: string, pageNumber: number) => {
    return groups?.[documentId]?.[pageNumber];
  };

  const handlePageLoad = (page: any, fullPage: number) => {
    if (fullPage === 1) {
      setFirstPageLoaded(true);
    }
    props.handlePageLoad(page, fullPage);
  };

  let fullPage = 0;

  return (
    <div className={`${classes.container} ds--pdf-container`}>
      {!firstPageLoaded && (
        <Skeleton
          className={classes.skeleton}
          variant="rect"
          animation="wave"
          height={SKELETON_HEIGHT * zoom}
          width={SKELETON_WIDTH * zoom}
        />
      )}
      {documents.map((doc: IDocument, documentIndex: number) => {
        const document = documents.find(({ order }) => order === documentIndex + 1);
        return (
          <Document
            key={document!.documentId}
            className={`${classes.document} ${firstPageLoaded ? '' : classes.hidden} ${
              isMobile ? classes.documentPadding : ''
            }`}
            loading=""
            file={pdfCache[document!.documentId]}
            onLoadError={(error: any) => console.error(error)}
            externalLinkTarget="_blank"
          >
            {[...Array(document!.numberOfPages)].map((_, pageNumber) => {
              fullPage++;
              const pageDimensions = document!.pages ? document!.pages[pageNumber] : props.pageDimensions[fullPage];
              return (
                <PdfPage
                  key={pageNumber}
                  documentIndex={documentIndex}
                  documentId={document!.documentId}
                  documentPages={document!.numberOfPages}
                  envelope={envelope}
                  blocks={blocks[document!.documentId]?.[pageNumber] || {}}
                  clickSignature={props.clickSignature.bind(null, document!.documentId, pageNumber)}
                  updateBlock={props.updateBlock.bind(null, document!.documentId, pageNumber)}
                  groups={getGroups(document!.documentId, pageNumber)}
                  scrollToNextBlock={props.scrollToNextBlock}
                  allBlocksComplete={props.allBlocksComplete}
                  tabToBlock={props.tabToBlock}
                  signingExperienceComplete={signingExperienceComplete}
                  currentSelectedBlockIndex={currentSelectedBlockIndex}
                  signingExperienceBlocks={signingExperienceBlocks}
                  fullPage={fullPage}
                  pageNumber={pageNumber}
                  zoom={zoom}
                  onLoadSuccess={handlePageLoad}
                  totalDocuments={props.documents.length}
                  documentName={props.documents[documentIndex].fileName}
                  totalPages={document!.numberOfPages}
                  pageDimensions={pageDimensions || { height: NaN, width: NaN }}
                  visiblePage={visiblePage}
                  setVisiblePage={handleSetVisiblePage}
                  pageScale={props.pageScale[fullPage]}
                  setPageScale={props.setPageScale}
                  signatureSettings={props.signatureSettings}
                  scrollToBlock={scrollToBlock}
                  setBlocksInGroupToComplete={props.setBlocksInGroupToComplete}
                  isMobile={isMobile}
                />
              );
            })}
          </Document>
        );
      })}
    </div>
  );
};

export default PdfViewer;
